// Day tasks: the shared jobs a tech can be put on besides operations (docs/today-tasks-plan.md).
// Pure functions, no Firebase — the techs app, the dock app, the Scheduler and the unit tests
// (tests/unit/dayTasks.test.mjs) all use them; dayTasks.js does the reads and writes.
//
// An assignment is "these people, on this day, on this task". Where each kind is stored:
//   haul         haulLaunchOps/duty/{date}/techs/{uid}          (a full day, existing)
//   moorings     haulLaunchOps/mooringDuty/{date}/techs/{uid}   (a full day, existing)
//   battery      batteries/schedule/assign/{date}                (the day's charging list)
//   launchcheck  launchChecks/assign/{unit}/{kind}               (stands until that check is finished)
//   ust-daily, ust-weekly, ust-monthly, uscg:{unit}, equip:{unit}:{service}
//                dayTasks/{date}/{key}
// Every record holds its people as techs/{uid} = { name, by, at, self? } (self: they took it from
// More rather than the office putting them on). Battery and launch-check records written before
// 2026-10-24 hold ONE person as top-level { uid, name, by, at }; assigneesOf() reads both.

/** The task kinds, in the order the More screen and Scheduler › Tasks list them. */
export const TASK_TYPES = {
  haul:          { label: 'Haul & launch',        es: 'Izar y botar',              fullDay: true },
  moorings:      { label: 'Mooring crew',         es: 'Equipo de amarres',         fullDay: true },
  battery:       { label: 'Battery charging',     es: 'Carga de baterías',         hours: 1.5 },
  'ust-daily':   { label: 'UST daily checks',     es: 'Revisión diaria UST',       hours: 0.25, ust: 'C' },
  'ust-weekly':  { label: 'UST weekly checks',    es: 'Revisión semanal UST',      hours: 0.5, ust: 'AB' },
  'ust-monthly': { label: 'UST monthly inspection', es: 'Inspección mensual UST',  hours: 1, ust: 'AB' },
  launchcheck:   { label: 'Launch check',         es: 'Revisión de lancha',        hours: 1, sticky: true },
  uscg:          { label: 'USCG inspection prep', es: 'Preparar inspección USCG',  hours: 2 },
  equip:         { label: 'Equipment maintenance', es: 'Mantenimiento de equipo',  hours: 1 },
};
export const TASK_KEYS = Object.keys(TASK_TYPES);

// ─── keys ─────────────────────────────────────────────────────────────────

/** 'launchcheck', ['LB-1', 'monthly'] → 'launchcheck:LB-1:monthly'. */
export function taskKey(type, ...parts) {
  if (!TASK_TYPES[type]) throw new Error(`Unknown task type: ${type}`);
  return [type, ...parts.map(p => String(p).replace(/[:.#$/[\]]/g, '-'))].join(':');
}

/** 'equip:FL-2:oil' → { type: 'equip', parts: ['FL-2', 'oil'] }; null for a key that isn't a task. */
export function parseTaskKey(key) {
  const [type, ...parts] = String(key || '').split(':');
  return TASK_TYPES[type] ? { type, parts } : null;
}

export function taskLabel(type, lang = 'EN') {
  const t = TASK_TYPES[type];
  return t ? (lang === 'ES' ? t.es : t.label) : '';
}

// ─── who may do what ──────────────────────────────────────────────────────

// Groups a person belongs to. tech = role token tech or yard; driver = launch driver or trainee;
// dockhand = dock hand or dockmaster (users/{uid}/crewRole). One person can be in several (Theo is a
// tech and a launch driver).
const roleTokens = role => String(role || '').toLowerCase().split(',').map(s => s.trim()).filter(Boolean);

export function groupsOf(person = {}) {
  const out = [];
  const tokens = roleTokens(person.role);
  if (tokens.includes('tech') || tokens.includes('yard')) out.push('tech');
  if (person.crewRole === 'driver' || person.crewRole === 'trainee') out.push('driver');
  if (person.crewRole === 'dockhand' || person.crewRole === 'dockmaster') out.push('dockhand');
  return out;
}

/** Who may be put on each kind (user, 2026-10-24). Settings › Team › Task rules can change it. */
export const DEFAULT_RULES = {
  haul:          { tech: true, driver: false, dockhand: false },
  moorings:      { tech: true, driver: false, dockhand: false },
  battery:       { tech: true, driver: false, dockhand: false },
  'ust-daily':   { tech: true, driver: true, dockhand: true },
  'ust-weekly':  { tech: true, driver: true, dockhand: true },
  'ust-monthly': { tech: true, driver: true, dockhand: true },
  launchcheck:   { tech: true, driver: true, dockhand: false },
  uscg:          { tech: true, driver: true, dockhand: false },
  equip:         { tech: true, driver: false, dockhand: false },
};

/** The marina's rules over the defaults (/settings/taskRules/{type}/{group} = true|false). */
export function rulesFor(type, overrides = {}) {
  return { ...(DEFAULT_RULES[type] || {}), ...((overrides && overrides[type]) || {}) };
}

// UST operator tag (users/{uid}/ustOperator): 'AB' (Class A/B, which covers C) or 'C'.
export const UST_LEVELS = { '': 'None', C: 'Class C', AB: 'Class A/B' };
export const ustLevel = v => (v === 'AB' || v === 'C' ? v : '');
const ustCovers = (has, needs) => !needs || has === 'AB' || (needs === 'C' && has === 'C');

/**
 * Can this person be put on this kind of task? person: { role, crewRole, ustOperator }.
 * Returns { ok, why } — why says, for More's "who does this" line, what they're missing.
 */
export function canDo(person, type, overrides = {}) {
  const t = TASK_TYPES[type];
  if (!t) return { ok: false, why: 'Unknown task' };
  const rules = rulesFor(type, overrides);
  const allowed = Object.keys(rules).filter(g => rules[g]);
  if (!groupsOf(person).some(g => rules[g])) {
    const names = { tech: 'techs', driver: 'launch drivers', dockhand: 'dock hands' };
    return { ok: false, why: `For ${allowed.map(g => names[g]).join(', ').replace(/, ([^,]*)$/, ' and $1') || 'nobody'}` };
  }
  if (!ustCovers(ustLevel(person.ustOperator), t.ust)) {
    return { ok: false, why: t.ust === 'AB' ? 'Needs a UST Class A/B operator' : 'Needs a UST Class C (or A/B) operator' };
  }
  return { ok: true, why: '' };
}

// ─── assignments ──────────────────────────────────────────────────────────

/** Everyone on an assignment record, either shape, sorted by name: [{ uid, name, by, at, self }]. */
export function assigneesOf(rec) {
  if (!rec || typeof rec !== 'object') return [];
  // Already read (normalizeAssignment): techs is the list.
  if (Array.isArray(rec.techs)) return rec.techs.map(t => ({ ...t }));
  const out = Object.entries(rec.techs || {})
    .filter(([, t]) => t && t.name)
    .map(([uid, t]) => ({ uid, name: t.name, by: t.by || '', at: t.at || 0, self: !!t.self }));
  if (rec.name && !out.some(t => (rec.uid && t.uid === rec.uid) || t.name === rec.name)) {
    out.push({ uid: rec.uid || '', name: rec.name, by: rec.by || '', at: rec.at || 0, self: false });
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

/** Is this person on it? By uid, or by name for an old record with no uid. */
export function isAssigned(rec, uid, name) {
  return assigneesOf(rec).some(t => (t.uid ? t.uid === uid : !!name && t.name === name));
}

/** "Brody, Tessa" — or '' when nobody is on it. */
export const assigneeNames = rec => assigneesOf(rec).map(t => t.name).join(', ');

/**
 * The record with its people read into one shape, for screens that show one line:
 * name = everyone's names, uid = the first person's, techs = the list. Other fields
 * (completedAt, order…) are kept. null when nobody is on it.
 */
export function normalizeAssignment(rec) {
  const techs = assigneesOf(rec);
  if (!techs.length) return null;
  const firstAt = Math.min(...techs.map(t => t.at || Infinity));
  return { ...rec, techs, name: techs.map(t => t.name).join(', '), uid: techs[0].uid, at: rec.at || (Number.isFinite(firstAt) ? firstAt : 0) };
}

/**
 * The multi-path patch that puts a person on a record at `path`, turning an old one-person
 * record into the techs/{uid} shape on the way. entry: { uid, name, by, at, self }.
 */
export function addPatch(path, rec, entry) {
  if (!entry || !entry.uid || !entry.name) throw new Error('A person needs a uid and a name');
  const patch = { [`${path}/techs/${entry.uid}`]: { name: entry.name, by: entry.by || '', at: entry.at || Date.now(), ...(entry.self ? { self: true } : {}) } };
  if (rec && rec.name && !rec.techs) {
    if (rec.uid && rec.uid !== entry.uid) patch[`${path}/techs/${rec.uid}`] = { name: rec.name, by: rec.by || '', at: rec.at || 0 };
    patch[`${path}/uid`] = null;
    patch[`${path}/name`] = null;
  }
  // When the record was first made: a launch check's assignment stands until a check finished after it.
  if (!rec || !rec.at) patch[`${path}/at`] = entry.at || Date.now();
  return patch;
}

/** The multi-path patch that takes a person off (an old one-person record by its uid or name). */
export function removePatch(path, rec, uid) {
  const patch = { [`${path}/techs/${uid}`]: null };
  if (rec && rec.name && !rec.techs && rec.uid === uid) { patch[`${path}/uid`] = null; patch[`${path}/name`] = null; }
  return patch;
}

// ─── hours ────────────────────────────────────────────────────────────────

/** Hours this task takes out of each person's day. The office can set a day's own (rec.hours). */
export function taskHours(type, rec = null, fallback = null) {
  const own = rec && Number(rec.hours);
  if (own > 0) return own;
  if (fallback != null && Number(fallback) > 0) return Number(fallback);
  return TASK_TYPES[type]?.hours || 0;
}

/** An operation's hours split between the techs on it (user, 2026-10-24): 4 h, 2 techs → 2 h each. */
export function splitHours(hours, techCount) {
  const h = Number(hours) || 0;
  const n = Math.max(1, Number(techCount) || 1);
  return Math.round((h / n) * 100) / 100;
}

// ─── carry-over ───────────────────────────────────────────────────────────

/**
 * This person's dated tasks for `today`: today's own, plus earlier days' ones that aren't done
 * (they carry over; the techs app never calls them late). byDate: { 'YYYY-MM-DD': { key: rec } };
 * isDone(key, rec, date) says whether a task is finished. Returns [{ key, type, date, rec, carried }],
 * one per key (the most recent day wins), today's first then oldest carry-over first.
 */
export function myDayTasks(byDate, today, uid, name, isDone = () => false) {
  const seen = new Map();
  Object.keys(byDate || {}).filter(d => d <= today).sort().forEach(date => {
    Object.entries(byDate[date] || {}).forEach(([key, rec]) => {
      const p = parseTaskKey(key);
      if (!p || !isAssigned(rec, uid, name)) return;
      if (date < today && isDone(key, rec, date)) { seen.delete(key); return; }
      seen.set(key, { key, type: p.type, date, rec, carried: date < today });
    });
  });
  return [...seen.values()]
    .filter(t => !(t.date === today && isDone(t.key, t.rec, t.date)))
    .sort((a, b) => (a.carried - b.carried) || a.date.localeCompare(b.date) || a.key.localeCompare(b.key));
}

// ─── done ─────────────────────────────────────────────────────────────────

/**
 * Is a UST task done? ruleItems: the marina's rule set items (complianceRulePacks); last:
 * /compliance/lastCompleted ({ itemId: { date } }); checks: /compliance/checks/{today}.
 * Daily checks belong to their day (a missed one doesn't follow the person to the next day);
 * weekly and monthly are done once every item of that cadence was checked on or after the day
 * the person was put on it.
 */
export function ustDone(type, date, today, ruleItems = [], last = {}, checks = {}) {
  const cadences = { 'ust-daily': ['daily'], 'ust-weekly': ['weekly', 'tenday'], 'ust-monthly': ['monthly'] }[type];
  if (!cadences) return false;
  const items = ruleItems.filter(i => cadences.includes(i.cadence));
  if (type === 'ust-daily') return date < today || (items.length > 0 && items.every(i => checks && checks[i.id]));
  return items.length > 0 && items.every(i => last && last[i.id] && last[i.id].date >= date);
}

/** Is this dated task done? Marked done (doneAt), or for UST, the checks are in (ustDone; ctx = { today, ruleItems, last, checks }). */
export function taskDone(key, rec, date, ctx = {}) {
  if (rec && rec.doneAt) return true;
  const p = parseTaskKey(key);
  if (p && p.type.startsWith('ust-')) return ustDone(p.type, date, ctx.today || date, ctx.ruleItems, ctx.last, ctx.checks);
  return false;
}

// ─── the Scheduler (office) ───────────────────────────────────────────────

/**
 * Hours each person's tasks take out of their day: { 'YYYY-MM-DD': { uid: hours } }.
 * dayTasks: /dayTasks ({ date: { key: rec } }); battery: /batteries/schedule/assign ({ date: rec }).
 * A task marked done still counts on its day (the time was spent). Haul/launch and mooring
 * crew days aren't here: they're full days (schedulerEngine.capFor gives them 0 hours).
 */
export function taskHoursByDay(dayTasks = {}, battery = {}) {
  const out = {};
  const add = (date, uid, h) => {
    if (!uid || !(h > 0)) return;
    const day = out[date] = out[date] || {};
    day[uid] = Math.round(((day[uid] || 0) + h) * 100) / 100;
  };
  Object.entries(dayTasks || {}).forEach(([date, tasks]) => Object.entries(tasks || {}).forEach(([key, rec]) => {
    const p = parseTaskKey(key);
    if (!p || TASK_TYPES[p.type].fullDay) return;
    assigneesOf(rec).forEach(t => add(date, t.uid, taskHours(p.type, rec)));
  }));
  Object.entries(battery || {}).forEach(([date, rec]) => assigneesOf(rec).forEach(t => add(date, t.uid, taskHours('battery', rec))));
  return out;
}

/** The day a UST weekly/monthly check next falls due ('YYYY-MM-DD'), from its items' last
 *  completions (/compliance/lastCompleted); null when the rule set has none of that cadence. */
export function ustNextDue(type, ruleItems = [], last = {}, today) {
  const cadences = { 'ust-weekly': ['weekly', 'tenday'], 'ust-monthly': ['monthly'] }[type];
  if (!cadences) return null;
  const dues = ruleItems.filter(i => cadences.includes(i.cadence) && i.periodDays).map(i => {
    const at = last && last[i.id] && last[i.id].date;
    if (!at) return today; // never done: due now
    const d = new Date(`${at}T00:00:00`);
    d.setDate(d.getDate() + i.periodDays);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  return dues.length ? dues.sort()[0] : null;
}

/** The marina has been running battery rounds lately: someone was put on one in the 14 days up to `today`. */
export function batteryRoundsRunning(battery = {}, today) {
  const from = new Date(`${today}T00:00:00`);
  from.setDate(from.getDate() - 14);
  const f = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}-${String(from.getDate()).padStart(2, '0')}`;
  return Object.entries(battery || {}).some(([d, rec]) => d >= f && d <= today && assigneesOf(rec).length > 0);
}

/**
 * The task kinds someone should be on that day (Scheduler › Tasks marks them amber when nobody
 * is; the Dashboard counts today's): the UST daily checks every day the fuel dock is on, the weekly
 * and monthly checks on the day they fall due (today if overdue), battery charging on weekdays while
 * rounds are running. on: { fuel, batteries } (marina features). Past days: none.
 */
export function dueTaskTypes({ date, today, on = {}, battery = {}, ruleItems = [], last = {} }) {
  if (date < today) return [];
  const out = [];
  if (on.fuel) {
    out.push('ust-daily');
    ['ust-weekly', 'ust-monthly'].forEach(type => {
      const next = ustNextDue(type, ruleItems, last, today);
      if (next && date === (next < today ? today : next)) out.push(type);
    });
  }
  const dow = new Date(`${date}T00:00:00`).getDay();
  if (on.batteries && dow >= 1 && dow <= 5 && batteryRoundsRunning(battery, today)) out.push('battery');
  return out;
}
